import { State } from '@progress/kendo-data-query';
import { CreateCartPoCartItem } from 'src/types/cart/createPo';
import { ShoppingCartItem, ShoppingCartSupplierListItem } from 'src/types/cart/shoppingCart';
import {
  ShoppingCartProduct,
  ShoppingCartProductSelection,
  ShoppingCartProductSelections,
} from 'src/types/shopping_cart/ShoppingCart';
import {
  AttributeFilterConfig,
  buildShoppingListProcessState,
  prepareGridDateFields,
} from '../shopping_lists/shoppingListProductMapper';

export {
  EMPTY_GRID_FILTER, extractAttributesFilter, prepareGridDateFields, removeFieldFromFilter
} from '../shopping_lists/shoppingListProductMapper';
export type { AttributeFilterConfig } from '../shopping_lists/shoppingListProductMapper';

export const buildShoppingCartProcessState = (dataState: State): State =>
  buildShoppingListProcessState(dataState);

export const buildShoppingCartAttributes = (item: ShoppingCartItem): string[] =>
  [
    item.isContracted ? 'isContracted' : null,
    item.isOfflineSupplierItem ? 'isOfflineSupplierItem' : null,
    item.isSpecialOrder ? 'isSpecialOrder' : null,
    item.isImageAvailable ? 'isImageAvailable' : null,
    item.isFormulary ? 'isFormulary' : null,
    item.productInFavoriteFolder ? 'productInFavoriteFolder' : null,
  ].filter((attribute): attribute is string => attribute !== null);

const productHasCartAttribute = (product: ShoppingCartProduct, attribute: string): boolean => {
  const attributeFlags: Record<string, boolean | undefined> = {
    isOfflineSupplierItem: product.isOfflineSupplierItem,
    isFormulary: product.isFormulary,
    isImageAvailable: product.isImageAvailable,
    isSpecialOrder: product.isSpecialOrder,
    isContracted: product.isContracted,
    productInFavoriteFolder: product.productInFavoriteFolder,
  };

  return attributeFlags[attribute] ?? product.attributes?.includes(attribute) ?? false;
};

export const applyShoppingCartAttributesFilter = (
  products: ShoppingCartProduct[],
  config: AttributeFilterConfig | null
): ShoppingCartProduct[] => {
  if (!config?.attributes.length) {
    return products;
  }

  return products.filter((product) => {
    if (config.operator === 'or') {
      return config.attributes.some((attribute) => productHasCartAttribute(product, attribute));
    }

    return config.attributes.every((attribute) => productHasCartAttribute(product, attribute));
  });
};

export const applyShoppingCartSupplierFilter = (
  products: ShoppingCartProduct[],
  suppliers: ShoppingCartSupplierListItem[],
  selectedSupplierIds: string[]
): ShoppingCartProduct[] => {
  if (!selectedSupplierIds.length) {
    return [];
  }

  const selectedSet = new Set(selectedSupplierIds);
  const selectedSuppliers = suppliers.filter((supplier) =>
    selectedSet.has(String(supplier.supplierClientId))
  );

  if (!selectedSuppliers.length) {
    return [];
  }

  return products.filter((product) =>
    selectedSuppliers.some((supplier) => productBelongsToSupplier(product, supplier))
  );
};

export const applyShoppingCartSearchTextFilter = (
  products: ShoppingCartProduct[],
  searchText: string
): ShoppingCartProduct[] => {
  const query = searchText.trim().toLowerCase();
  if (!query) {
    return products;
  }

  return products.filter((product) => {
    const itemNum = product.itemNum?.toLowerCase() ?? '';
    const description = product.description?.toLowerCase() ?? '';

    return itemNum.includes(query) || description.includes(query);
  });
};

export type GridReadyShoppingCartProduct = Omit<ShoppingCartProduct, 'lastPurchaseDate'> & {
  lastPurchaseDate: Date | null;
  lastPurchaseDateRaw: string | null;
};

/** Supplier card key — offline items use offlineSupplierId when present. */
export const getCartItemSupplierLookupId = (item: ShoppingCartItem): number => {
  if (item.isOfflineSupplierItem && item.offlineSupplierId != null) {
    return item.offlineSupplierId;
  }

  return item.supplierClientId;
};

export const resolveCartItemSupplierId = (item: ShoppingCartItem): number =>
  getCartItemSupplierLookupId(item);

export const resolveCartItemSupplierCompanyId = (
  item: ShoppingCartItem,
  supplierCompanyIdByClientId: Map<number, number>
): number =>
  supplierCompanyIdByClientId.get(getCartItemSupplierLookupId(item)) ?? item.supplierCompanyId ?? 0;

export const getShoppingCartProductKey = (
  catalogId: number,
  categoryId: number,
  productId: number,
  clientOrderBinId: number
): string => `${catalogId}-${categoryId}-${productId}-${clientOrderBinId}`;

export const getShoppingCartProductSelection = (
  selections: ShoppingCartProductSelections,
  productKey: string,
  cartQuantity: number
): ShoppingCartProductSelection => {
  const existing = selections[productKey];

  return {
    selected: existing?.selected ?? false,
    qty: existing?.qty ?? cartQuantity,
  };
};

export const resolveQtyOnProductSelection = (selected: boolean, qty: number): number => {
  if (selected && qty === 0) {
    return 1;
  }

  return qty;
};

/**
 * Header checkbox: checking selects only the rows on the current grid page, while unchecking
 * clears every cart line, including rows hidden by the search, column filters or paging.
 */
export const applyShoppingCartHeaderSelection = (
  selections: ShoppingCartProductSelections,
  pageProducts: ShoppingCartProduct[],
  allProducts: ShoppingCartProduct[],
  checked: boolean
): ShoppingCartProductSelections => {
  const cartQtyByKey = new Map(allProducts.map((product) => [product.productKey, product.quantity]));
  const targetProducts = checked ? pageProducts : allProducts;
  const next = { ...selections };

  targetProducts.forEach(({ productKey }) => {
    const current = getShoppingCartProductSelection(
      selections,
      productKey,
      cartQtyByKey.get(productKey) ?? 1
    );

    next[productKey] = {
      selected: checked,
      qty: resolveQtyOnProductSelection(checked, current.qty),
    };
  });

  return next;
};

/**
 * Rows hidden by the search box or grid filters count as deselected, while their stored
 * selection is kept so clearing the filters brings them back selected. Returns `selections`
 * itself when nothing selected is hidden.
 */
export const getVisibleShoppingCartSelections = (
  selections: ShoppingCartProductSelections,
  visibleProductKeys: ReadonlySet<string>
): ShoppingCartProductSelections => {
  const hiddenSelectedKeys = Object.keys(selections).filter(
    (productKey) => selections[productKey].selected && !visibleProductKeys.has(productKey)
  );

  if (hiddenSelectedKeys.length === 0) {
    return selections;
  }

  const next = { ...selections };
  hiddenSelectedKeys.forEach((productKey) => {
    next[productKey] = { ...selections[productKey], selected: false };
  });

  return next;
};

export const productBelongsToSupplier = (
  product: ShoppingCartProduct,
  supplier: ShoppingCartSupplierListItem
): boolean => {
  const supplierCardId = supplier.supplierClientId;

  if (supplier.isOfflineSupplier) {
    return (
      product.isOfflineSupplierItem &&
      (product.offlineSupplierId === supplierCardId ||
        product.supplierId === supplierCardId ||
        product.supplierClientId === supplierCardId)
    );
  }

  return !product.isOfflineSupplierItem && product.supplierClientId === supplierCardId;
};

export const getProductsForSupplier = (
  products: ShoppingCartProduct[],
  supplier: ShoppingCartSupplierListItem
): ShoppingCartProduct[] =>
  products.filter((product) => productBelongsToSupplier(product, supplier));

export const isShoppingCartSupplierSelected = (
  products: ShoppingCartProduct[],
  supplier: ShoppingCartSupplierListItem,
  selections: ShoppingCartProductSelections
): boolean =>
  getProductsForSupplier(products, supplier).some(
    (product) => selections[product.productKey]?.selected
  );

export const computeSelectedShoppingCartGridTotals = (
  products: ShoppingCartProduct[],
  selections: ShoppingCartProductSelections
): { totalQty: number; totalAmount: number } =>
  products.reduce(
    (totals, product) => {
      const selection = selections[product.productKey];
      if (!selection?.selected) {
        return totals;
      }

      const qty = selection.qty ?? product.quantity;


      return {
        totalQty: totals.totalQty + qty,
        totalAmount: totals.totalAmount + (product.total ?? 0),
      };
    },
    { totalQty: 0, totalAmount: 0 }
  );

export const prepareShoppingCartProductsForGrid = (
  products: ShoppingCartProduct[],
  getQty: (productKey: string, defaultQty: number) => number
): GridReadyShoppingCartProduct[] =>
  prepareGridDateFields(products).map((product) => {
    const qty = getQty(product.productKey, product.quantity);
    return {
      ...product,
      quantity: qty,
    };
  });

export const mapShoppingCartProductToCreatePoCartItem = (
  product: ShoppingCartProduct
): CreateCartPoCartItem => ({
  clientOrderBinId: product.clientOrderBinId,
  supplierClientId: product.supplierClientId,
  offlineSupplierID: product.offlineSupplierId ?? 0,
});

export const mapShoppingCartApiItem = (
  item: ShoppingCartItem,
  supplierCompanyId: number
): ShoppingCartProduct => {
  const supplierId = resolveCartItemSupplierId(item);

  return {
    productKey: getShoppingCartProductKey(item.catalogId, item.categoryId, item.productId, item.clientOrderBinId),
    productId: item.productId,
    catalogId: item.catalogId,
    categoryId: item.categoryId,
    clientOrderBinId: item.clientOrderBinId,
    supplierClientId: item.supplierClientId,
    offlineSupplierId: item.offlineSupplierId,
    supplierCompanyId,
    supplierId,
    supplierCompanyName: item.supplierCompanyName?.trim() ?? '',
    itemNum: item.itemNumber?.trim() ?? '',
    description: item.description?.trim() ?? '',
    uom: item.uom?.trim().toUpperCase() ?? '',
    minimumQuantity: item.minimumQuantity?.trim() ?? '',
    quantity: item.quantity ?? 0,
    price: item.price,
    priceFormatted: item.priceFormatted ?? '',
    weight: item.weight?.trim() ?? '',
    casePack: item.casePack?.trim() ?? '',
    catalogName: item.catalogName?.trim() ?? '',
    priceGroupId: item.priceGroupId,
    total: item.total,
    totalFormatted: item.totalFormatted ?? '',
    glCode: item.glCode?.trim() ?? '',
    lastPurchaseDate: item.lastPurchaseDate?.trim() ?? null,
    isBelowMinimumQuantity: item.isBelowMinimumQuantity ?? '',
    productInFavoriteFolder: item.productInFavoriteFolder,
    isFormulary: item.isFormulary,
    isImageAvailable: item.isImageAvailable,
    isSpecialOrder: item.isSpecialOrder,
    isContracted: item.isContracted,
    isNoItem: item.isNoItem,
    isOfflineSupplierItem: item.isOfflineSupplierItem,
    attributes: buildShoppingCartAttributes(item),
  };
};
